import React, { useMemo, useState } from "react";
import { Calculator as CalcIcon } from "lucide-react";
import { brl } from "@/lib/api";

const N = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

export default function Calculadora() {
  const [f, setF] = useState({ custo: 100, impostos: 6, frete: 0, embalagem: 0, despesas: 5, comissao: 0, margem: 20 });
  const set = (k, v) => setF({ ...f, [k]: v });

  const r = useMemo(() => {
    const custo = N(f.custo) + N(f.frete) + N(f.embalagem);
    const pctSum = N(f.impostos) + N(f.despesas) + N(f.comissao) + N(f.margem);
    // preço tal que preço = custoFixo + preço*(pct/100)
    const denom = 1 - pctSum / 100;
    const precoVenda = denom > 0 ? custo / denom : 0;
    const precoMinimo = 1 - (N(f.impostos) + N(f.despesas) + N(f.comissao)) / 100 > 0
      ? custo / (1 - (N(f.impostos) + N(f.despesas) + N(f.comissao)) / 100) : 0;
    const impostosVal = precoVenda * (N(f.impostos) / 100);
    const despesasVal = precoVenda * (N(f.despesas) / 100);
    const comissaoVal = precoVenda * (N(f.comissao) / 100);
    const lucro = precoVenda - custo - impostosVal - despesasVal - comissaoVal;
    const margemEfetiva = precoVenda > 0 ? (lucro / precoVenda) * 100 : 0;
    return { custo, precoVenda, precoMinimo, lucro, margemEfetiva };
  }, [f]);

  const INPUTS = [
    ["custo", "Custo do produto (R$)"], ["frete", "Frete (R$)"], ["embalagem", "Embalagem (R$)"],
    ["impostos", "Impostos (%)"], ["despesas", "Despesas (%)"], ["comissao", "Comissão (%)"],
    ["margem", "Margem desejada (%)"],
  ];

  return (
    <div className="animate-fade-up">
      <div className="mb-4">
        <h1 className="font-display text-2xl font-bold text-slate-900">Calculadora de Preço</h1>
        <p className="text-sm text-slate-500">Simule preço de venda, lucro e margem para suas propostas de licitação.</p>
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2"><CalcIcon className="h-5 w-5 text-sky-600" /><h3 className="font-display font-semibold text-slate-800">Parâmetros</h3></div>
          <div className="grid grid-cols-2 gap-3">
            {INPUTS.map(([k, label]) => (
              <div key={k}>
                <label className="mb-1 block text-xs font-semibold text-slate-600">{label}</label>
                <input data-testid={`calculator-input-${k}`} type="number" value={f[k]} onChange={(e) => set(k, e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono outline-none focus:border-sky-400" />
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-3">
          <div className="rounded-xl bg-gradient-to-br from-slate-900 to-sky-950 p-6 text-white shadow-lg">
            <div className="text-xs font-semibold uppercase tracking-wide text-sky-300">Preço de venda sugerido</div>
            <div data-testid="calculator-output-price" className="mt-1 font-display text-4xl font-bold">{brl(r.precoVenda)}</div>
            <div className="mt-1 text-sm text-slate-300">Custo total: {brl(r.custo)}</div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              ["Preço mínimo", brl(r.precoMinimo), "text-slate-900", "calculator-output-min"],
              ["Lucro", brl(r.lucro), r.lucro >= 0 ? "text-emerald-600" : "text-rose-600", "calculator-output-profit"],
              ["Margem efetiva", `${r.margemEfetiva.toFixed(1)}%`, "text-sky-600", "calculator-output-margin"],
            ].map(([l, v, c, tid]) => (
              <div key={l} className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm">
                <div className="text-[11px] font-semibold uppercase text-slate-400">{l}</div>
                <div data-testid={tid} className={`mt-1 font-display text-lg font-bold ${c}`}>{v}</div>
              </div>
            ))}
          </div>
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
            O preço mínimo cobre custos e impostos sem margem. Abaixo dele a operação dá prejuízo.
          </p>
        </div>
      </div>
    </div>
  );
}
